import { ArrowForwardIcon, CheckCircleIcon } from '@chakra-ui/icons'
import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { groupBy, map, pipe, sortBy, values } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { SalesGroup } from '~shared-components/api/sales-groups'
import { Input } from '~shared-components/components'
import { ChangeSalesOrgModal } from '~shared-components/components/Modal/ChangeSalesOrgModal'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { IconChevronDown } from '~shared-components/icons/brand/IconChevronDown'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import { trimLeadingZeros } from '~shared-components/utils/misc'

import { LayoutPadding } from '../../theme'
import { CustomerContext } from '../../transforms/user.transforms'

interface CustomerSelectorProps {
  customers: CustomerContext[] | undefined
  onChange: (context: CustomerContext) => void
}
interface Customer {
  contextId: string
  name: string
  number: string
}
interface SalesOrgPermission {
  customer: Customer
  salesGroup: SalesGroup
}

interface CustomerSelectorOption {
  customers: Customer[]
  salesGroup: SalesGroup
}

const CLEAR_VALUE = ''
const INCREASE_POPOVER_WIDTH_AFTER = 50
const SHOW_SEARCH_INPUT_AFTER = 10
const TRUNCATE_CUSTOMER_NAME_AT = 40

const makeActiveContextName = (isMobile: boolean) => (
  customer: Customer | undefined,
  salesGroup: SalesGroup | undefined,
) =>
  customer && salesGroup
    ? isMobile
      ? `${customer.name} - ${trimLeadingZeros(customer.number)} | ${
          salesGroup.name
        } (${salesGroup.salesOrganization})`
      : `${
          customer.name.length > TRUNCATE_CUSTOMER_NAME_AT
            ? customer.name.slice(0, TRUNCATE_CUSTOMER_NAME_AT) + '…'
            : customer.name
        } | ${salesGroup.salesOrganization}`
    : ''

export const CustomerSelector = ({
  customers,
  onChange,
}: CustomerSelectorProps) => {
  const t = useTranslations()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [
    activeSalesOrganisationName,
    setActiveSalesOrganisationName,
  ] = useState<string>('')
  const [salesGroup, setSalesGroup] = useState<SalesGroup | undefined>()
  const [customer, setCustomer] = useState<Customer | undefined>()
  const [options, setOptions] = useState<CustomerSelectorOption[] | undefined>()
  const selectedContext: CustomerContext | undefined = useSelector(
    LoginSelector.selectedCustomerContext,
  )

  const isLongVersionSelector = useBreakpointValue([false, true]) as boolean
  const getActiveContextName = makeActiveContextName(isLongVersionSelector)
  const {
    isOpen: isModalOpen,
    onClose: modalClose,
    onOpen: modalOpen,
  } = useDisclosure()

  const onChangeActiveSalesOrg = (
    salesGroup: SalesGroup,
    customer: Customer,
  ) => {
    const selectedCustomer: CustomerContext | undefined = customers?.find(
      (p: CustomerContext) =>
        p.customerNumber === customer.number &&
        p.salesGroup.id === salesGroup.id,
    )
    if (selectedCustomer) {
      setActiveSalesOrganisationName(getActiveContextName(customer, salesGroup))

      onChange?.(selectedCustomer)
    }
  }

  const onChangeActiveSalesOrgModal = (
    salesGroup: SalesGroup,
    customer: Customer,
  ) => {
    setSalesGroup(salesGroup)
    setCustomer(customer)
    modalOpen()
  }

  const handleModalConfirm = () => {
    onChangeActiveSalesOrg(salesGroup!, customer!)
    modalClose()
  }

  const isActiveItem = (c: Customer, s: SalesGroup): boolean =>
    activeSalesOrganisationName === getActiveContextName(c, s)

  const permissionTransform = (p: CustomerContext): SalesOrgPermission => ({
    customer: {
      contextId: p.customerContextId,
      name: p.customerName,
      number: p.customerNumber,
    },
    salesGroup: p.salesGroup,
  })

  const salesOrgGroupBy = (option: SalesOrgPermission) =>
    option.salesGroup.salesOrganization

  const salesGroupWithCustomerArrayTransform = (
    options: SalesOrgPermission[],
  ) => ({
    customers: options.map(o => o.customer),
    salesGroup: options[0].salesGroup,
  })

  const optionsPipeline = pipe(
    map(permissionTransform),
    sortBy(salesOrgGroupBy),
    groupBy(salesOrgGroupBy),
    values,
    map(salesGroupWithCustomerArrayTransform),
  )

  const dropdownOptions: CustomerSelectorOption[] | undefined = useMemo(
    () => (customers ? optionsPipeline(customers) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers],
  )

  const onSearch = (value: string) => {
    const filteredOptions = dropdownOptions
      ?.map(({ customers, salesGroup }) => ({
        customers: customers.filter(customer =>
          [
            salesGroup.salesOrganization,
            salesGroup.name,
            customer.name,
            customer.number,
          ].some(entity => entity.toLowerCase().includes(value)),
        ),
        salesGroup,
      }))
      .filter(option => option.customers.length > 0)
    setOptions(filteredOptions)
  }

  const clearInput = () => {
    // this is due to the animation for hiding the popup
    setTimeout(() => {
      if (inputRef && inputRef.current) {
        inputRef.current.value = CLEAR_VALUE
      }
      onSearch(CLEAR_VALUE)
    }, 100)
  }

  useEffect(() => {
    if (!dropdownOptions) {
      return
    }

    const permission =
      dropdownOptions.find(
        o => o.salesGroup.id === selectedContext?.salesGroup.id,
      ) || dropdownOptions?.[0]

    if (!permission) {
      return
    }

    const customer =
      permission.customers.find(
        o => o.contextId === selectedContext?.customerContextId,
      ) || permission.customers?.[0]

    setActiveSalesOrganisationName(
      getActiveContextName(customer, permission.salesGroup),
    )

    clearInput()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dropdownOptions,
    selectedContext?.customerContextId,
    isLongVersionSelector,
  ])

  if (!(customers && customers.length >= 2)) return null

  const allCustomers = dropdownOptions?.map(({ customers }) => customers).flat()

  const hasLongCustomerNames = allCustomers
    ?.map(({ name, number }: Customer) => name + ' | ' + number)
    .some((name: string) => name.length >= INCREASE_POPOVER_WIDTH_AFTER)

  const hasSearch = (allCustomers?.length ?? 0) > SHOW_SEARCH_INPUT_AFTER

  return (
    <LayoutPadding
      align="flex-end"
      bg="neutral.10"
      height={10}
      justify="center"
      maxHeight={10}
      mb={[0, 0]}
      px={[2, 12, 24]}
      py={[2, 5]}
    >
      <Flex zIndex={1000}>
        <Popover matchWidth placement="bottom-end">
          {({ onClose }) => (
            <>
              <PopoverTrigger>
                <Text
                  color="neutral.80"
                  cursor="pointer"
                  fontSize={['12px', 'md']}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {activeSalesOrganisationName}{' '}
                  <IconChevronDown boxSize="16px" color="neutral.50" />
                </Text>
              </PopoverTrigger>
              <PopoverContent
                _focus={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                }}
                bg="neutral.10"
                border="1px solid"
                borderColor="neutral.20"
                borderRadius="4px"
                maxHeight={['100vh', 'lg']}
                maxWidth={['100vw', 'md', hasLongCustomerNames ? 'xl' : 'lg']}
                minHeight={['100vh', 'inherit']}
                minWidth={['100vw', 'md', hasLongCustomerNames ? 'xl' : 'lg']}
                onBlur={clearInput}
                overflowY="scroll"
              >
                <PopoverCloseButton
                  display={['block', 'none']}
                  right="1px"
                  top="-4px"
                />
                <PopoverBody p={6}>
                  {hasSearch && (
                    <Box mb={3}>
                      <Input
                        ref={inputRef}
                        showClearButton
                        onValueChange={onSearch}
                        placeholder={t('filter.search-salesorg.placeholder')}
                        type="search"
                      />
                    </Box>
                  )}
                  <VStack spacing={4}>
                    {options?.map(({ customers, salesGroup }) => (
                      <Flex
                        key={salesGroup.id}
                        direction="column"
                        fontSize="md"
                        w="full"
                      >
                        <Flex
                          border="1px solid"
                          borderColor="neutral.20"
                          px={2}
                          py={3}
                          w="full"
                        >
                          <Text color="neutral.90">
                            {salesGroup.name} ({salesGroup.salesOrganization})
                          </Text>
                        </Flex>
                        {customers.map((customer: Customer) => {
                          const isActiveOption = isActiveItem(
                            customer,
                            salesGroup,
                          )
                          return (
                            <Flex
                              key={customer.contextId}
                              align="center"
                              cursor="pointer"
                              justify="space-between"
                              onClick={() => {
                                onClose()
                                onChangeActiveSalesOrgModal(
                                  salesGroup,
                                  customer,
                                )
                              }}
                              px={2}
                              py={3}
                              sx={
                                isActiveOption
                                  ? {
                                      background: 'neutral.10',
                                      borderLeft: '6px solid',
                                      borderLeftColor: 'deepPurple',
                                      color: 'deepPurple',
                                      fontWeight: 'bold',
                                      paddingLeft: '18px',
                                    }
                                  : {
                                      background: 'white',
                                      color: 'neutral.70',
                                      paddingLeft: '24px',
                                    }
                              }
                              w="full"
                            >
                              <Text>
                                {customer.name} -{' '}
                                {trimLeadingZeros(customer.number)}
                              </Text>
                              <Flex ml={2}>
                                {isActiveOption ? (
                                  <CheckCircleIcon color="deepPurple" w={4} />
                                ) : (
                                  <ArrowForwardIcon color="neutral.50" w={4} />
                                )}
                              </Flex>
                            </Flex>
                          )
                        })}
                      </Flex>
                    ))}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>
      </Flex>
      <ChangeSalesOrgModal
        isOpen={isModalOpen}
        onClose={modalClose}
        onConfirm={handleModalConfirm}
      />
    </LayoutPadding>
  )
}
